import { DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { DashboardComponent } from './_components/dashboard/dashboard.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, registerLocaleData } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AlarmComponent } from './_components/_header/alarm/alarm.component';
import { MessageManagerComponent } from './_components/message-manager/message-manager.component';
import { MonitoringComponent } from './_components/_header/monitoring/monitoring.component';
import { SessionComponent } from './_components/_header/session/session.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { EsListComponent } from './_components/_widgets/es-list/es-list.component';
import { MatTableModule } from '@angular/material/table';
import { HeaderComponent } from './_components/_widgets/header/header.component';
import { FooterComponent } from './_components/_widgets/footer/footer.component';
import { MapVecComponent } from './_components/_widgets/map-vec/map-vec.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SpDialogEditComponent } from './_components/_dialogs/sp-dialog-edit/sp-dialog-edit.component';
import { PgmDialogEditComponent } from './_components/_dialogs/pgm-dialog-edit/pgm-dialog-edit.component';
import { SpListComponent } from './_components/_widgets/sp-list/sp-list.component';
import { PgmListComponent } from './_components/_widgets/pgm-list/pgm-list.component';
import { AvancementListComponent } from './_components/_widgets/avancement-list/avancement-list.component';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDialogModule } from '@angular/material/dialog';
import {
  ExcelModule,
  GridModule,
  PDFModule,
} from '@progress/kendo-angular-grid';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  CheckBoxModule,
  ColorPickerModule,
  TextBoxModule,
} from '@progress/kendo-angular-inputs';
import { IntlModule } from '@progress/kendo-angular-intl';

// Load all required data for the bg locale
import '@progress/kendo-angular-intl/locales/fr-CH/all';
import { AlarmeListComponent } from './_components/_widgets/alarme-list/alarme-list.component';
import { MapEditComponent } from './_components/_widgets/map-edit/map-edit.component';
import { MatMenuModule } from '@angular/material/menu';
import { EsDialogEditComponent } from './_components/_dialogs/es-dialog-edit/es-dialog-edit.component';
import { MatRadioModule } from '@angular/material/radio';
import { FocusMeDirective } from './_directives/focus-me.directive';
import { SubsystemReportComponent } from './_components/_widgets/map-edit/subsystem-report/subsystem-report.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { PopupModule } from '@progress/kendo-angular-popup';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { AlarmeEffect } from './_store/AlarmeStore/alarme.effect';
import { reducers } from './_store/mainStoreState';
import { AdminConsoleComponent } from './_components/admin-console/admin-console.component';
import { MinimaxiDirective } from './_directives/minimaxi.directive';
import { AdminConsoleRowComponent } from './_components/admin-console/admin-console-row/admin-console-row.component';
import { EvenementSpontaneEffect } from './_store/EvenementSpontaneStore/evenement-spontane.effect';
import { ScenarioPreprogrammeEffect } from './_store/ScenarioPreprogrammeStore/scenario-preprogramme.effect';
import { EtapeScenarioEffect } from './_store/EtapeStore/etape.effect';
import { PlanGestionMobiliteEffect } from './_store/PlanGestionMobiliteStore/plan-gestion-mobilite.effect';
import { SousSystemeEffect } from './_store/SousSystemeStore/sous-systeme.effect';
import { EquipementEffect } from './_store/EquipementStore/equipement.effect';
import { ColoredCircleTextComponent } from './_components/_header/colored-circle-text/colored-circle-text.component';
import { SpDialogEtapeListComponent } from './_components/_dialogs/sp-dialog-etape-list/sp-dialog-etape-list.component';
import { AssociationSegmentEffect } from './_store/AssociationSegmentStore/association-segment.effect';
import { MatDividerModule } from '@angular/material/divider';
import { UserProfileComponent } from './_components/_header/user-profile/user-profile.component';
import { MapGenericButtonComponent } from './_components/map-generic-button/map-generic-button.component';
import { MatSliderModule } from '@angular/material/slider';
import { ContainerRefDirective } from './_directives/container-ref.directive';
import { FlyingContainerComponent } from './_components/flying-container/flying-container.component';
import { EsInfoComponent } from './_components/_widgets/map-edit/es-info/es-info.component';
import { RapportSousSystemeEffect } from './_store/RapportSousSystemeStore/rapport-sous-systeme.effect';
import { ConfirmDialogComponent } from './_components/_widgets/confirm-dialog/confirm-dialog.component';
import { MatTabsModule } from '@angular/material/tabs';
import { TabEtapesComponent } from './_components/_dialogs/sp-dialog-edit/tab-etapes/tab-etapes.component';
import { TabGeometryComponent } from './_components/_dialogs/sp-dialog-edit/tab-geometry/tab-geometry.component';
import { MapEditionGeometryComponent } from './_components/map-edition-geometry/map-edition-geometry.component';
import { MapEditionSelectOverlayComponent } from './_components/map-edition-select-overlay/map-edition-select-overlay.component';
import { AvancementEffect } from './_store/AvancementStore/avancement.effect';
import { EquipmentListComponent } from './_components/_widgets/equipement-list/equipment-list.component';
import { AccountEffect } from './_store/AccountStore/account.effect';
import { RouterModule } from '@angular/router';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { DigitOnlyDirective } from './_directives/digit-only.directive';
import { ArxLoadingDirective } from './_directives/arx-loading.directive';
import { DashboardControlsComponent } from './_components/dashboard-controls/dashboard-controls.component';
import { LogListComponent } from './_components/_widgets/log-list/log-list.component';
import { LogsEffect } from './_store/LogsStore/logs.effect';
import { HistoriqueComponent } from './_components/_widgets/historique/historique.component';
import { HistoriqueFormComponent } from './_components/_widgets/historique/historique-form/historique-form.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { HistoriqueSliderComponent } from './_components/_widgets/historique/historique-slider/historique-slider.component';
import 'hammerjs';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { LabelModule } from '@progress/kendo-angular-label';
import '@progress/kendo-angular-intl/locales/fr-CH/all';
import localFrCh from '@angular/common/locales/fr-CH';

import { MulticheckFilterComponent } from './_components/multicheck-filter/multicheck-filter.component';
import { SessionEditionEffect } from './_store/SessionStore/session.effect';
import { LoginComponent } from './_components/login/login.component';
import { DashboardContainerComponent } from './_components/dashboard-container/dashboard-container.component';
import { AppRoutingModule } from './app-routing.module';
import { HttpErrorInterceptor } from './_services/http/http-error-interceptor';

registerLocaleData(localFrCh);

const ANGULAR_MODULES = [
  CommonModule,
  MatIconModule,
  MatIconModule,
  MatButtonModule,
  MatTooltipModule,
  MatBadgeModule,
  MatToolbarModule,
  MatTableModule,
  MatProgressSpinnerModule,
  MatSelectModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatDialogModule,
  MatExpansionModule,
  MatMenuModule,
  MatRadioModule,
  MatSnackBarModule,
  DragDropModule,
  FlexLayoutModule,
  MatDividerModule,
  MatSliderModule,
  MatTabsModule,
  MatRippleModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatProgressBarModule,
  MatCheckboxModule,
];

const KENDO_MODULES = [
  GridModule,
  TextBoxModule,
  ExcelModule,
  PDFModule,
  IntlModule,
  PopupModule,
  ColorPickerModule,
  MultiSelectModule,
  LabelModule,
  CheckBoxModule,
];

const WIDGET_COMPONENTS = [
  HeaderComponent,
  FooterComponent,
  EsListComponent,
  MapVecComponent,
  MapEditComponent,
  SpListComponent,
  PgmListComponent,
  EquipmentListComponent,
  SpDialogEditComponent,
  PgmDialogEditComponent,
  AvancementListComponent,
];

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    AlarmComponent,
    MessageManagerComponent,
    MonitoringComponent,
    SessionComponent,
    ...WIDGET_COMPONENTS,
    AlarmeListComponent,
    EsDialogEditComponent,
    FocusMeDirective,
    SubsystemReportComponent,
    AdminConsoleComponent,
    MinimaxiDirective,
    AdminConsoleRowComponent,
    ColoredCircleTextComponent,
    SpDialogEtapeListComponent,
    UserProfileComponent,
    MapGenericButtonComponent,
    ContainerRefDirective,
    FlyingContainerComponent,
    EsInfoComponent,
    ConfirmDialogComponent,
    TabEtapesComponent,
    TabGeometryComponent,
    MapEditionGeometryComponent,
    MapEditionSelectOverlayComponent,
    DigitOnlyDirective,
    ArxLoadingDirective,
    DashboardControlsComponent,
    LogListComponent,
    HistoriqueComponent,
    HistoriqueFormComponent,
    HistoriqueSliderComponent,
    HistoriqueSliderComponent,
    MulticheckFilterComponent,
    LoginComponent,
    DashboardContainerComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    ...ANGULAR_MODULES,
    ...KENDO_MODULES,
    PopupModule,
    StoreModule.forRoot(reducers),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
    }),
    EffectsModule.forRoot([
      AlarmeEffect,
      EvenementSpontaneEffect,
      ScenarioPreprogrammeEffect,
      EtapeScenarioEffect,
      PlanGestionMobiliteEffect,
      SousSystemeEffect,
      EquipementEffect,
      AssociationSegmentEffect,
      RapportSousSystemeEffect,
      AccountEffect,
      AvancementEffect,
      LogsEffect,
      SessionEditionEffect,
    ]),
    NotificationModule,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'fr-CH',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CHF',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },{
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
